import { useNavigate } from '@tanstack/react-router'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AlertTriangle, CalendarClock, ListChecks, Lock } from 'lucide-react'
import { formatDateHuman } from '@/lib/utils'

interface PreviousDayBlockerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Optional message from the API explaining which date is still open. */
  message?: string
  /** Whether the user has permission to navigate to /admin/day-close. */
  canRunDayClose: boolean
}

/**
 * Pulls the unclosed business date out of the backend's 409 message.
 * Backend formats it as `... (open day=YYYY-MM-DD, today=YYYY-MM-DD)` —
 * we only surface the open day, since that's the one the user must close.
 */
function extractOpenDay(message?: string): string | null {
  if (!message) return null
  const match = message.match(/open day=(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : null
}

/**
 * Whole calendar days between `isoDate` (YYYY-MM-DD in local time) and today.
 * Returns null for unparseable input or future dates — the modal only ever
 * shows past open days, so a non-positive diff means we should suppress
 * the suffix rather than render "(0 days ago)".
 */
function daysSince(isoDate: string): number | null {
  const [y, m, d] = isoDate.split('-').map(Number)
  if (!y || !m || !d) return null
  const then = new Date(y, m - 1, d).getTime()
  const now = new Date()
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const diff = Math.round((todayLocal - then) / 86_400_000)
  return diff >= 1 ? diff : null
}

/**
 * Hard block shown to counter staff when checkout fails with HTTP 409
 * `previous_day_open`. The counter cannot keep ringing sales until the
 * last open business day is closed — revenue would otherwise attribute
 * to the wrong day. The gap can be anything from 1 day to many days,
 * so the copy below must not assume it's literally yesterday.
 */
export function PreviousDayBlockerModal({
  open,
  onOpenChange,
  message,
  canRunDayClose,
}: PreviousDayBlockerModalProps) {
  const navigate = useNavigate()
  const openDay = extractOpenDay(message)
  const openDayLabel = openDay ? formatDateHuman(openDay, { alwaysYear: true }) : null
  const gap = openDay ? daysSince(openDay) : null
  const gapSuffix = gap === 1 ? 'yesterday' : gap !== null ? `${gap} days ago` : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl text-rose-700 dark:text-rose-300">
            <AlertTriangle className="h-6 w-6" /> One day still needs closing
          </DialogTitle>
          <DialogDescription className="text-base">
            We can't take new orders today until the last open day is finished and locked. This
            keeps your sales counted on the right day.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <section className="rounded-lg border border-rose-200 bg-rose-50 p-4 dark:border-rose-900/60 dark:bg-rose-950/40">
            <div className="flex items-center gap-2 text-rose-800 dark:text-rose-200">
              <CalendarClock className="h-4 w-4" />
              <h3 className="text-sm font-semibold">What happened</h3>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-rose-900 dark:text-rose-100">
              {openDayLabel ? (
                <>
                  The day for <span className="font-semibold">{openDayLabel}</span>
                  {gapSuffix ? <> <span className="text-rose-700/80 dark:text-rose-200/80">({gapSuffix})</span></> : null}{' '}
                  was never finished. Until it's locked, today can't start and no new sales can
                  be taken — even if that day was a while back.
                </>
              ) : (
                <>
                  An earlier day was never finished. Until it's locked, today can't start and no
                  new sales can be taken.
                </>
              )}
            </p>
          </section>

          <section className="rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-foreground">
              <ListChecks className="h-4 w-4" />
              <h3 className="text-sm font-semibold">How to fix it</h3>
            </div>
            {canRunDayClose ? (
              <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
                <li>
                  Tap <span className="font-medium text-foreground">Go to Daily Closing</span>{' '}
                  below.
                </li>
                <li>
                  Count the money in the drawer for{' '}
                  {openDayLabel ? (
                    <span className="font-medium text-foreground">{openDayLabel}</span>
                  ) : (
                    'that day'
                  )}{' '}
                  and tap Finish the day.
                </li>
                <li>Today will start automatically with the next sale.</li>
              </ol>
            ) : (
              <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
                <li>
                  Ask a manager or owner to finish the day on this device for{' '}
                  {openDayLabel ? (
                    <span className="font-medium text-foreground">{openDayLabel}</span>
                  ) : (
                    'the open day'
                  )}
                  .
                </li>
                <li>They'll count the cash and lock the day.</li>
                <li>Your shift will start again automatically once they're done.</li>
              </ol>
            )}
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Dismiss
          </Button>
          {canRunDayClose ? (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                onOpenChange(false)
                navigate({ to: '/admin/day-close' })
              }}
            >
              <Lock className="mr-2 h-4 w-4" /> Go to Daily Closing
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
